import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { URL_PROXY } from './apiConfig';
import './Admin.css';

const api = axios.create({ baseURL: URL_PROXY, withCredentials: true });

const Admin = () => {
  const [authState, setAuthState] = useState('checking');
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [settings, setSettings] = useState({ bookingEnabled: true, priceAdjustmentPercent: 0 });
  const [savedSettings, setSavedSettings] = useState(settings);
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState('');

  const loadDashboard = useCallback(async () => {
    const response = await api.get('/admin/dashboard');
    setBookings(response.data.bookings);
    setSettings(response.data.settings);
    setSavedSettings(response.data.settings);
  }, []);

  useEffect(() => {
    api.get('/admin/session').then(async () => {
      setAuthState('authenticated');
      await loadDashboard();
    }).catch(() => setAuthState('login'));
  }, [loadDashboard]);

  const login = async (event) => {
    event.preventDefault();
    setLoading(true); setLoginError('');
    try {
      await api.post('/admin/login', credentials);
      setAuthState('authenticated');
      setCredentials({ username: '', password: '' });
      await loadDashboard();
    } catch (error) {
      setLoginError(error.response?.data?.message || 'Login failed.');
    } finally { setLoading(false); }
  };

  const logout = async () => {
    try { await api.post('/admin/logout'); } finally { setAuthState('login'); setBookings([]); }
  };

  const saveSettings = async () => {
    setLoading(true); setMessage('');
    try {
      const response = await api.put('/admin/settings', {
        bookingEnabled: settings.bookingEnabled,
        priceAdjustmentPercent: Number(settings.priceAdjustmentPercent),
      });
      setSettings(response.data.settings); setSavedSettings(response.data.settings);
      setMessage('Settings saved.');
    } catch (error) { setMessage(error.response?.data?.message || 'Settings could not be saved.'); }
    finally { setLoading(false); }
  };

  const updateBooking = (updated) => setBookings((current) => current.map((item) => item._id === updated._id ? updated : item));
  const complete = async (id) => { const response = await api.post(`/admin/bookings/${id}/complete`); updateBooking(response.data.booking); };
  const cancel = async (id) => {
    if (!window.confirm('Cancel this reservation and send cancellation emails?')) return;
    await api.post(`/admin/bookings/${id}/cancel`);
    setBookings((current) => current.map((item) => item._id === id ? { ...item, status: 'CANCELLED' } : item));
  };

  const filteredBookings = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return bookings;
    return bookings.filter((booking) => [booking._id, booking.firstName, booking.lastName, booking.email, booking.origin, booking.destination].some((value) => String(value || '').toLowerCase().includes(query)));
  }, [bookings, search]);

  if (authState === 'checking') return <main className="adminPage"><div className="adminLoading">Checking session…</div></main>;
  if (authState === 'login') return (
    <main className="adminPage adminLoginPage"><form className="adminLoginCard" onSubmit={login}>
      <span className="adminEyebrow">Secure administration</span><h1>Welcome back.</h1><p>Sign in to manage bookings and pricing.</p>
      <label>Username<input autoComplete="username" value={credentials.username} onChange={(e) => setCredentials({ ...credentials, username: e.target.value })} required /></label>
      <label>Password<input type="password" autoComplete="current-password" value={credentials.password} onChange={(e) => setCredentials({ ...credentials, password: e.target.value })} required /></label>
      {loginError && <div className="adminError" role="alert">{loginError}</div>}
      <button type="submit" disabled={loading}>{loading ? 'Signing in…' : 'Sign in'}</button>
    </form></main>
  );

  const settingsChanged = settings.bookingEnabled !== savedSettings.bookingEnabled || Number(settings.priceAdjustmentPercent) !== Number(savedSettings.priceAdjustmentPercent);
  return <main className="adminPage"><div className="adminDashboard">
    <header className="adminHeader"><div><span className="adminEyebrow">Moroder Taxi</span><h1>Admin dashboard</h1></div><button onClick={logout}><LogoutRoundedIcon />Logout</button></header>
    <section className="adminSettingsGrid">
      <article className="adminPanel adminAvailability"><div><span>Online booking</span><h2>{settings.bookingEnabled ? 'Accepting bookings' : 'Bookings paused'}</h2><p>Disabling this blocks price calculations and new reservations.</p></div><label className="adminSwitch"><input type="checkbox" checked={settings.bookingEnabled} onChange={(e) => setSettings({ ...settings, bookingEnabled: e.target.checked })} /><span /></label></article>
      <article className="adminPanel adminPricing"><div><span>Overall price adjustment</span><h2>{Number(settings.priceAdjustmentPercent) > 0 ? '+' : ''}{settings.priceAdjustmentPercent}%</h2><p>Example: €100 becomes €{Math.round(100 * (1 + Number(settings.priceAdjustmentPercent) / 100))}.</p></div><input type="range" min="-20" max="30" step="1" value={settings.priceAdjustmentPercent} onChange={(e) => setSettings({ ...settings, priceAdjustmentPercent: Number(e.target.value) })} /><input className="adminPercent" type="number" min="-20" max="30" value={settings.priceAdjustmentPercent} onChange={(e) => setSettings({ ...settings, priceAdjustmentPercent: Number(e.target.value) })} /></article>
      <div className="adminSettingsSave">{message && <span>{message}</span>}<button disabled={!settingsChanged || loading} onClick={saveSettings}>Save settings</button></div>
    </section>
    <section className="adminBookings"><div className="adminBookingsHead"><div><span className="adminEyebrow">Operations</span><h2>Current bookings <b>{filteredBookings.length}</b></h2></div><label className="adminSearch"><SearchRoundedIcon /><input placeholder="Search customer, route or ID" value={search} onChange={(e) => setSearch(e.target.value)} /></label></div>
      <div className="adminBookingList">{filteredBookings.length === 0 ? <div className="adminEmpty">No matching bookings.</div> : filteredBookings.map((booking) => <article className={`adminBookingCard status-${booking.status?.toLowerCase()}`} key={booking._id}>
        <div className="adminBookingTop"><div><span className="adminStatus">{booking.status}</span><h3>{booking.firstName} {booking.lastName}</h3><code>{booking._id}</code></div><strong>€ {Number(booking.price).toFixed(2)}</strong></div>
        <div className="adminBookingInfo"><div><small>Journey</small><b>{booking.origin}</b><span>→ {booking.destination}</span></div><div><small>Travel details</small><b>{booking.date} · {booking.time}</b><span>{booking.persons} passengers</span></div><div><small>Contact</small><b>{booking.phonenumber}</b><span>{booking.email}</span></div><div><small>Extras / information</small><b>{booking.amenities?.join(', ') || 'None'}</b><span>{booking.info || 'No additional information'}</span></div></div>
        <div className="adminBookingActions"><a href={`tel:${booking.phonenumber}`}><PhoneOutlinedIcon />Call</a><a href={`mailto:${booking.email}`}><EmailOutlinedIcon />Email</a><button onClick={() => complete(booking._id)} disabled={booking.status === 'COMPLETED'}><CheckCircleOutlineRoundedIcon />Complete</button><button className="danger" onClick={() => cancel(booking._id)} disabled={booking.status === 'CANCELLED'}><CancelOutlinedIcon />Cancel</button></div>
      </article>)}</div>
    </section>
  </div></main>;
};

export default Admin;
